<template>
    <div class="edit-customer">
        <div class="edit-customer__container spacing-b-6">
            <TitleText class="spacing-b-6" level="1">
                {{ $ml.get('editCustomerTitle') }}
            </TitleText>
    
            <CieloInputV2
                class="personal-data-input"
                :autofocus="false"
                :borderless="false"
                name="Email"
                :label="$ml.get('email')"
                v-model="localCustomer.email"
                v-validate="'required|safe-mail'"
                :data-vv-as="$ml.get('email')"
                type="email"
                :error="errors.first('Email')"
                :disabled="true"
            />
    
            <CieloInputV2
                :autofocus="true"
                :borderless="false"
                name="Name"
                :label="$ml.get('fullName')"
                v-model="localCustomer.fullName"
                v-validate="'required|fullname'"
                :data-vv-as="$ml.get('fullName')"
                type="text"
                :error="errors.first('Name')"
                class="personal-data-input"
            />
    
            <CieloInputV2
                :autofocus="false"
                :borderless="false"
                name="Phone"
                :label="$ml.get('cellPhone')"
                v-model="localCustomer.phone"
                v-validate="'required|numeric|min:11'"
                :data-vv-as="$ml.get('cellPhone')"
                type="tel"
                :error="errors.first('Phone')"
                mask="(##) #####-####"
            />
    
            <div class="edit-customer__wrapper--identity">
                <CieloSelectV2
                    @change="selectDocumentType"
                    :title="$ml.get('documentType')"
                    :label="$ml.get('selectDocumentType')"
                    :option-items="documentTypes"
                    :selected-item="localCustomer.identityType"
                    other-class="cielo-select__container--documentType"
                    :can-translate="true"
                />
                <CieloInputV2
                    style="width: 100%; margin-bottom: 0px"
                    :autofocus="false"
                    :borderless="false"
                    name="Identity"
                    :label="$ml.get('identity')"
                    v-model="localCustomer.identity"
                    v-validate="identityValidationRule"
                    :data-vv-as="$ml.get('identity')"
                    :type="identityInputType"
                    :error="errors.first('Identity')"
                    :mask="identityMask"
                />
            </div>
        </div>

        <div class="edit-customer__cta-container">
            <ButtonsNextPreviousV2
                :disable-button-next="disableButtonSaveAndContinue"
                :loading="loading"
                :back-text="'cancel'"
                :next-text="'saveAndContinue'"
                @click-next="saveAndContinue"
                @click-previous="goToPreviousStep"
            />
        </div>
    </div>
</template>

<script>
	import { mapGetters } from 'vuex';

	import CpfCnpjValidation from '@/helpers/validation-rules/CpfCnpjValidation';
	import FullNameValidation from '@/helpers/validation-rules/FullNameValidation';
	import SafeMailValidation from '@/helpers/validation-rules/SafeMailValidation';

	const CieloInputV2 = () =>
		import(
			/* webpackChunkName: "cielo-input" */ '@/components/ClickToPay/template/CieloInputV2'
		);
	import TitleText from '@/components/template/TitleText';
	import CieloSelectV2 from '@/components/ClickToPay/template/CieloSelectV2/CieloSelectV2';
    import ButtonsNextPreviousV2 from '@/components/template/ButtonsNextPreviousV2';

	export default {
		components: {
			CieloInputV2,
			TitleText,
			CieloSelectV2,
            ButtonsNextPreviousV2,
		},

		data() {
			return {
				localCustomer: {},
				documentTypes: [
					{ Text: 'cpf', Value: 'CPF' },
					{ Text: 'cnpj', Value: 'CNPJ' },
					{ Text: 'foreign', Value: 'FOREIGN' },
				],
                loading: false,
			};
		},

		created() {
			this.localCustomer = JSON.parse(JSON.stringify(this.customer));
		},

		mounted() {
			this.$validator.extend('cpf-cnpj', CpfCnpjValidation);
			this.$validator.extend('fullname', FullNameValidation);
			this.$validator.extend('safe-mail', SafeMailValidation);
		},

		computed: {
			...mapGetters({
				customer: 'dataOnFile/getCustomer',
			}),

            identityValidationRule() {
				switch (this.localCustomer.identityType) {
					case 'CPF':
						return 'required|numeric|min:11|max:11|cpf-cnpj';
					case 'CNPJ':
						return 'required|numeric|min:14|max:14|cpf-cnpj';
					default:
						return 'required|max:32';
				}
			},
			identityMask() {
				switch (this.localCustomer.identityType) {
					case 'CPF':
						return ['###.###.###-##'];
					case 'CNPJ':
						return ['##.###.###/####-##'];
					default:
						return null;
				}
			},
			identityInputType() {
				return ['CPF', 'CNPJ'].includes(this.localCustomer.identityType) ? 'tel' : 'text';
			},

            disableButtonSaveAndContinue() {
                return false;
            },
		},

		methods: {
			selectDocumentType(value) {
				this.localCustomer.identityType = value;
			},

            goToPreviousStep() {
                this.$store.dispatch('pipelineV2/pushTo', 'DataOnFileReview');
            },

            async saveAndContinue() {
                if(this.loading) return;
                this.loading = true;

                let isValid = await this.$validator.validateAll();

                if(!isValid) {
                    this.loading = false;
                    return;
                }

                try {
                    await this.$store.dispatch('dataOnFile/editCustomer', this.localCustomer);
                    this.$store.dispatch('pipelineV2/pushTo', 'DataOnFileReview');
                } catch (error) {
                    // TODO: tratar o erro na view?
                    console.error(error);
                } finally {
                    this.loading = false;
                }
            },
		},
	};
</script>

<style scoped lang="scss">
    .edit-customer {
        &__wrapper--identity {
            display: flex;
			gap: 20px;
			width: 100%;
            
			@media screen and (max-width: 1024px) {
				flex-direction: column;
				margin-top: 0px;
			}
        }
    }
</style>
